/**
 * Catalog domain types shared by the control API and the web application.
 *
 * Asset (logical identity) → AssetVersion (revision) → Representation
 * (original / proxy / thumbnail / sprite / waveform) → FileLocation (where a
 * representation physically lives). These are deliberately distinct.
 */
import type { ProbeResult } from './jobs.ts';
import type { LocationAvailability, StorageKind } from './locations.ts';

export type VersionStatus = 'registered' | 'recording' | 'processing' | 'ready' | 'partial' | 'failed';
export type RunStatus = 'queued' | 'running' | 'succeeded' | 'partial' | 'failed' | 'cancelled';
export type StepStatus = 'pending' | 'running' | 'succeeded' | 'skipped' | 'failed';
/** 1.2.0: `master`, a further master of the version (masters.ts); `rendition`, a workflow transcode's output. */
export type RepresentationKind = 'original' | 'master' | 'proxy' | 'thumbnail' | 'sprite' | 'waveform' | 'rendition';
/**
 * Whether an asset's original can be read: online, offline, unknown, or (0.29.0) archived (only in an archive
 * tier, which needs a restore) or restoring (a restore from the archive is under way).
 */
export type AssetAvailability = LocationAvailability | 'archived' | 'restoring';

/** A restore of an original from an archive tier (0.29.0). */
export interface RestoreDTO {
  id: string;
  assetId: string;
  assetTitle: string;
  assetCode: string;
  versionId: string;
  /** queued → restoring (the store prepares the object) → copying (to the originals tier) → succeeded, or failed. */
  status: 'queued' | 'restoring' | 'copying' | 'succeeded' | 'failed';
  /** Why: the Restore button, Restore and export, or Restore and start. */
  reason: 'manual' | 'export' | 'workflow';
  /** Retrieval tier (AWS: Expedited, Standard, Bulk); null for OCI. */
  tier: string | null;
  /** How long the store keeps the restored object readable. */
  days: number;
  source: { id: string; name: string; storageClass: string | null };
  destination: { id: string; name: string };
  /** What the store usually takes, e.g. "3–5 hours". */
  expectedDuration: string;
  requestedBy: string | null;
  error: ProcessingErrorDTO | null;
  createdAt: string;
  requestedAt: string | null;
  availableAt: string | null;
  finishedAt: string | null;
  transferId: string | null;
}

export const ATTACHMENT_KINDS = ['subtitle', 'graphics', 'document', 'image', 'audio', 'metadata', 'other'] as const;
export type AttachmentKind = (typeof ATTACHMENT_KINDS)[number];
export const ATTACHMENT_KIND_LABELS: Record<AttachmentKind, string> = {
  subtitle: 'Subtitles', graphics: 'Graphics', document: 'Document', image: 'Image', audio: 'Audio', metadata: 'Metadata', other: 'Other',
};

/** Guess an attachment's kind from its file name. */
export function attachmentKindFor(filename: string): AttachmentKind {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  if (['srt', 'vtt', 'scc', 'stl', 'ttml', 'dfxp', 'ass', 'ssa', 'sub'].includes(ext)) return 'subtitle';
  if (['psd', 'ai', 'eps', 'svg', 'aep', 'mogrt', 'html'].includes(ext)) return 'graphics';
  if (['pdf', 'doc', 'docx', 'odt', 'rtf', 'txt', 'md', 'pages', 'fdx', 'fountain', 'xls', 'xlsx', 'csv', 'ppt', 'pptx'].includes(ext)) return 'document';
  if (['jpg', 'jpeg', 'png', 'tif', 'tiff', 'gif', 'webp', 'bmp', 'heic'].includes(ext)) return 'image';
  if (['wav', 'mp3', 'aac', 'm4a', 'flac', 'aif', 'aiff'].includes(ext)) return 'audio';
  if (['xml', 'json', 'ale', 'edl', 'xmp', 'yaml', 'yml'].includes(ext)) return 'metadata';
  return 'other';
}

export interface AttachmentDTO {
  id: string;
  kind: AttachmentKind;
  filename: string;
  mimeType: string | null;
  sizeBytes: number;
  checksumSha256: string;
  storageName: string;
  source: 'upload' | 'watch_folder' | 'workflow';
  createdBy: string | null;
  createdAt: string;
  url: string;
}

export interface TransferDTO {
  id: string;
  representationId: string;
  representationKind: RepresentationKind;
  sourceName: string;
  destinationName: string;
  /** cancelled (1.1.0): stopped by a storage migration's pause, window end or cancel. */
  status: 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';
  bytes: number | null;
  /** How far a running copy is (0.95.0): bytes copied over the file's size; null while unknown. */
  percent: number | null;
  /** Queued (0.95.0): the copy waits for a free transfer worker (since waitingSince). */
  waiting: boolean;
  waitingSince: string | null;
  requestedBy: string | null;
  reason: string | null;
  error: ProcessingErrorDTO | null;
  createdAt: string;
  finishedAt: string | null;
}

export interface FileLocationDTO {
  storageLocationId: string;
  storageName: string;
  storageKind: StorageKind;
  site: string;
  availability: LocationAvailability;
  key: string;
  sizeBytes: number | null;
  status: 'present' | 'missing';
  /** false when PRISM manages the file where it already lived (agent discovery). */
  managed: boolean;
  /** In an archive tier (0.29.0): archived, restoring, or readable until expiresAt. Absent or null elsewhere. */
  archive?: { state: 'archived' | 'restoring' | 'available'; expiresAt: string | null } | null;
}

export interface RepresentationDTO {
  id: string;
  kind: RepresentationKind;
  preset: string;
  mimeType: string | null;
  sizeBytes: number | null;
  checksumSha256: string | null;
  attributes: Record<string, unknown>;
  createdAt: string;
  locations: FileLocationDTO[];
}

export interface ProcessingStepDTO {
  step: string;
  label: string;
  status: StepStatus;
  attempts: number;
  startedAt: string | null;
  finishedAt: string | null;
  error: ProcessingErrorDTO | null;
  detail: Record<string, unknown>;
}

export interface ProcessingErrorDTO {
  code: string;
  message: string;
  retryable: boolean;
  hint?: string;
}

export interface ProcessingRunDTO {
  id: string;
  /** 1.5.0: 'shots' is a Detect shots run (its one step 'sprite', "Shots and scrub images"). */
  kind: 'ingest' | 'shots';
  attempt: number;
  status: RunStatus;
  workflowId: string;
  correlationId: string;
  requestedBy: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  error: ProcessingErrorDTO | null;
  steps: ProcessingStepDTO[];
}

export interface SpriteLayout {
  intervalSeconds: number;
  columns: number;
  rows: number;
  count: number;
  tileWidth: number;
  tileHeight: number;
  /**
   * 1.5.0 (a sprite from shots): when each tile was taken, in seconds of the proxy, ascending, one per tile. Absent:
   * a timed sprite (a tile every intervalSeconds). Read it with spriteTileAt (shots.ts), never by index maths.
   */
  tileSeconds?: number[];
  /** 1.5.0: per tile, true for a forced tile inside a long shot, false for a shot's first frame. */
  forced?: boolean[];
}

/** What the summary status can be: a version's status, or 'placeholder' for an asset without media yet. */
export type AssetStatus = VersionStatus | 'placeholder';

/** ASSET_<year>_<8 digits>: the permanent ID every asset gets. */
export const ASSET_CODE_PATTERN = /^ASSET_\d{4}_\d{8}$/;

export interface AssetSummary {
  id: string;
  /** The permanent human-readable ID, e.g. ASSET_2026_00000042. */
  assetCode: string;
  title: string;
  /** The asset's category (Programme, Clip, …), if any. */
  category: { id: string; name: string; color: string } | null;
  status: AssetStatus;
  dismissed: boolean;
  /** graphic: an HTML5 graphics template uploaded as a .zip, used on a sequence's graphics track. */
  mediaKind: 'video' | 'audio' | 'image' | 'graphic' | null;
  durationTicks: number | null;
  /** Status recording (1.0.0-rc.1, in lists): the length recorded so far, which the editor can use. */
  recordedTicks?: number | null;
  frameRate: { num: number; den: number } | null;
  width: number | null;
  height: number | null;
  /** 1.0.0-rc.11: progressive or interlaced (from the current version's probe); null when not video or unknown. */
  scanType?: 'progressive' | 'interlaced' | null;
  /** 1.0.0-rc.11: an interlaced video's field shown first; null otherwise. */
  fieldOrder?: 'tff' | 'bff' | null;
  /** Null for a placeholder (no media yet). */
  originalFilename: string | null;
  sizeBytes: number | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  thumbnailUrl: string | null;
  spriteUrl: string | null;
  spriteLayout: SpriteLayout | null;
  progress: { step: string; label: string; percent: number | null } | null;
  error: ProcessingErrorDTO | null;
  duplicateOf: string | null;
  metadata: Record<string, unknown>;
  availability: AssetAvailability;
  /** True when the asset is restricted to access groups. */
  restricted: boolean;
  /** True when a visible field flagged Needed (or Required) is empty. Recomputed on every metadata or class change and model publish. */
  metadataIncomplete: boolean;
  /** Automated QC of the current version (1.0.0-rc.8): a run in progress, else the latest that performed QC; null when none. */
  /** `status` is the engine's result; `effectiveStatus` is passed once every finding is accepted (`resolved` says by whom). */
  qc?: { status: import('./qc.ts').QcRunStatus; effectiveStatus: import('./qc.ts').QcRunStatus; resolved: import('./qc.ts').QcResolution | null; runId: string } | null;
  /** Set while the asset waits in the recycle bin (or its undo window) before it is purged. */
  deleted: DeletedInfo | null;
  /**
   * 1.2.0: the current version's masters in short (formats and variants, the primary included), for the Library's
   * badge ("UHD · HD · TXL"); null or absent when unknown (no media).
   */
  masterSummary?: import('./masters.ts').MasterSummaryDTO | null;
  /**
   * 1.4.0: the asset's groups, one per type ("Series › Season 1 · Episode 3"; the Library's Series, Season and
   * Episode no. columns). Filled by the groups lookup (catalog/groups/library.ts, G1); absent before it is built.
   */
  groups?: import('./groups.ts').AssetGroupRef[];
  /**
   * 1.4.0: values inherited from the asset's groups, by asset field id, only for fields whose own value is empty
   * (never written into metadata; shown in grey "from Series"). Absent or empty when it inherits nothing.
   */
  inheritedMetadata?: Record<string, import('./groups.ts').InheritedValue>;
  /** 1.8.0: the asset's effective rights (its own, inherited and from its sources); absent while rights management is off. */
  rights?: import('./rights.ts').RightsSummary;
}

export interface DeletedInfo {
  at: string;
  by: string | null;
  /** When the purge becomes due. */
  purgeAfter: string;
  /** Why the last purge attempt could not finish (e.g. storage offline); it is retried. */
  purgeError: string | null;
}

export interface RecycleBinSettingsDTO {
  enabled: boolean;
  retentionDays: number;
  /** With the bin switched off, deletes can still be undone for this long. */
  undoSeconds: number;
  updatedAt: string | null;
}

export interface AssetGraphicDTO {
  /** The template id sequences refer to (asset-<asset id>), and its version (the asset version's number). */
  templateId: string;
  version: number;
  /** The manifest as registered, with the field defaults people set in PRISM. */
  manifest: import('./graphics.ts').TemplateManifest;
  /** The package's own id and version (manifest.json), for reference. */
  packageId: string | null;
  packageVersion: number | string | null;
  checksumSha256: string;
  /** False while the asset is in the recycle bin (or after it was purged): hidden from the graphics picker. */
  enabled: boolean;
  /** 'renderat' or 'ograf-v1' (0.93.0); an OGraf graphic's field defaults are set in PRISM Graphics, not here. */
  engine?: import('./ograf.ts').GraphicEngine;
  /** The OGraf manifest as it is in the package (OGraf only). */
  ograf?: import('./ograf.ts').OgrafManifest;
}

export interface AssetDetail extends AssetSummary {
  workspaceId: string;
  /** Access groups this asset is restricted to; empty means everyone with access to the workspace. */
  restrictedTo: Array<{ id: string; name: string }>;
  /** Null for a placeholder (no media yet). */
  versionId: string | null;
  versionNumber: number | null;
  /** 1.2.3: how many versions the asset has that are not purged (earlier ones in the recycle bin included). Absent before 1.2.3. */
  versionCount?: number;
  /** When PRISM last used this version (playback, download, export, processing; 0.98.0). Null: never, or a placeholder. */
  lastAccessedAt: string | null;
  metadataRevision: number;
  /** The visible fields (that the caller may see) which are Needed or Required and still empty; behind the incomplete-metadata warning. */
  neededMissing: string[];
  /** Values preserved when the metadata model changed underneath them. */
  metadataArchive: Record<string, { value: unknown; reason: string; fromVersion: number; label: string }>;
  technical: ProbeResult | null;
  representations: RepresentationDTO[];
  /**
   * 1.2.0: the current version's masters, the primary first, then by variant order and format (largest first).
   * Absent from servers before 1.2.0; empty for a placeholder.
   */
  masters?: import('./masters.ts').MasterDTO[];
  runs: ProcessingRunDTO[];
  /** 1.5.0: the current version's shots ("42 shots · detected with sensitivity 50"); null or absent: never detected. */
  shots?: import('./shots.ts').ShotsSummary | null;
  /** A live ingest: recording now (or just finished), with its live proxy. */
  live: import('./live.ts').LiveDTO | null;
  /** A graphics asset: the template its current version registered (used on sequences' graphics tracks). */
  graphic: AssetGraphicDTO | null;
  /** 1.8.0: whether the asset has a genealogy (a made asset: Made from, and the Rights tab's From its sources). Absent before 1.8.0. */
  hasGenealogy?: boolean;
  media: {
    proxyUrl: string | null;
    posterUrl: string | null;
    spriteUrl: string | null;
    waveformUrl: string | null;
    originalUrl: string | null;
    expiresAt: string;
  };
  timing: {
    sourceFrameRate: { num: number; den: number } | null;
    proxyFrameRate: { num: number; den: number } | null;
    startTimecode: string | null;
    normalizedFromVfr: boolean;
    previewAccuracy: 'approximate';
  };
}

export type DuplicatePolicy = 'ask' | 'allow' | 'reject';

export interface UploadDTO {
  id: string;
  filename: string;
  sizeBytes: number;
  receivedBytes: number;
  status: 'open' | 'complete' | 'aborted';
  chunkSize: number;
  assetId: string | null;
}

/* ---------------------------------------------------------------- categories and relations */

export interface AssetCategoryDTO {
  id: string;
  name: string;
  color: string;
  position: number;
  /** Assets in this category (not deleted). */
  assets: number;
}

/** Directed relations: the asset `from` <kind> the asset `to`. */
export const RELATION_KINDS = ['part_of', 'trailer_for', 'uses_footage', 'version_of', 'related'] as const;
export type RelationKind = (typeof RELATION_KINDS)[number];
/** How a relation reads from each end. */
export const RELATION_LABELS: Record<RelationKind, { from: string; to: string }> = {
  part_of: { from: 'Part of', to: 'Contains' },
  trailer_for: { from: 'Trailer for', to: 'Has trailer' },
  uses_footage: { from: 'Uses footage from', to: 'Footage used in' },
  version_of: { from: 'Version of', to: 'Has version' },
  related: { from: 'Related to', to: 'Related to' },
};

export interface AssetRelationDTO {
  id: string;
  kind: RelationKind;
  /** Whether this asset is the relation's `from` end. */
  outgoing: boolean;
  /** How it reads from this asset, e.g. "Part of". */
  label: string;
  other: { id: string; title: string; thumbnailUrl: string | null; mediaKind: string | null; category: { id: string; name: string; color: string } | null; deleted: boolean };
  note: string | null;
  createdBy: string | null;
  createdAt: string;
  /** 1.8.0: 'system' for the uses_footage rows PRISM wrote before 1.8.0 (they can't be removed: RELATION_LOCKED); absent: 'user'. */
  origin?: 'user' | 'system';
}
